import { useSession } from 'next-auth/react'
import { type FC, type PropsWithChildren, useMemo } from 'react'

import { ALL_CANADA_LOCATION_ID } from '@/constants/location'
import { getLocationPath } from '@/domain/location/getLocationPath'
import { useGetHomeFeedAdsQuery } from '@/generated/graphql-types'
import useCookiePreferences from '@/hooks/useCookiePreferences'
import { useLocale } from '@/hooks/useLocale'

import { createHomepageGptConfig } from '../ads-config/homepage/createHomepageGptConfig'
import { generateGptTargetingValues } from '../ads-config/homepage/generateGptTargetingValues'
import { prebidConfig } from '../components/prebid/prebidConfig'
import { getIsAuthenticatedForGpt } from '../utils/gptTargeting'
import { AdsWrapper } from './AdsWrapper'

type HomepageAdsWrapperProps = PropsWithChildren & {
  locationId: number
  shouldShowHomepageFeed: boolean
}

export const HomepageAdsWrapper: FC<HomepageAdsWrapperProps> = ({
  locationId,
  shouldShowHomepageFeed,
  children,
}) => {
  const { routeLocale } = useLocale()
  const { status } = useSession()
  const isSignedIn = getIsAuthenticatedForGpt(status)
  const { hasUserOptedOutTargeting = false } = useCookiePreferences()

  const { data: homeFeedAdsData } = useGetHomeFeedAdsQuery()
  const numOfHomeFeedAds = homeFeedAdsData?.homeFeedAds.adSlots || 0

  const locationPaths =
    locationId === ALL_CANADA_LOCATION_ID ? undefined : getLocationPath(locationId)

  const gptTargetingValues = useMemo(
    () =>
      generateGptTargetingValues(
        isSignedIn,
        routeLocale,
        locationId,
        locationPaths,
        hasUserOptedOutTargeting
      ),
    [isSignedIn, routeLocale, locationId, locationPaths, hasUserOptedOutTargeting]
  )

  const homepageGptConfig = useMemo(
    () =>
      createHomepageGptConfig(
        gptTargetingValues,
        routeLocale,
        shouldShowHomepageFeed,
        numOfHomeFeedAds
      ),
    [gptTargetingValues, routeLocale, shouldShowHomepageFeed, numOfHomeFeedAds]
  )

  /**
   * We need to make React-Advertising wait for the GPT scripts to be loaded
   * Otherwise it will throw a window.googletag.cmd undefined error
   */
  return (
    <AdsWrapper
      gptConfig={homepageGptConfig}
      gptTargetingValues={gptTargetingValues}
      prebidConfig={prebidConfig}
      skipAdsense
      skipBlockthrough
    >
      {children}
    </AdsWrapper>
  )
}
